import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const features = [
  { icon: "🎬", en: "500K+ Episodes", zh: "海量短剧内容" },
  { icon: "🌏", en: "Multi-Language", zh: "多语言支持" },
  { icon: "📴", en: "Offline Watch", zh: "离线观看" },
  { icon: "🤖", en: "AI Recommendations", zh: "AI个性推荐" },
  { icon: "💎", en: "VIP Membership", zh: "会员专属内容" },
  { icon: "🔔", en: "New Episode Alerts", zh: "新剧更新提醒" },
];

// Minimal SVG phone mockup with screen content
const PhoneMockup = ({ accent, delay }: { accent: string; delay: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.8 }}
    style={{ position: "relative" }}
  >
    <style>{`
      @keyframes screenGlow{0%,100%{box-shadow:0 0 30px ${accent}33,0 30px 60px rgba(0,0,0,0.6)}50%{box-shadow:0 0 50px ${accent}55,0 30px 80px rgba(0,0,0,0.7)}}
      @keyframes barSlide{0%{width:0}100%{width:var(--w)}}
      @keyframes phoneFloat{0%,100%{transform:translateY(0px) rotate(var(--rot,0deg))}50%{transform:translateY(-12px) rotate(var(--rot,0deg))}}
    `}</style>
    {/* Phone shell */}
    <div style={{
      width: 200, borderRadius: 32,
      background: "linear-gradient(160deg,#1a1a1a,#0d0d0d)",
      border: "1.5px solid rgba(255,255,255,0.12)",
      padding: "14px 10px",
      boxShadow: `0 0 30px ${accent}33,0 30px 60px rgba(0,0,0,0.6)`,
      animation: "phoneFloat 5s ease-in-out infinite",
      ["--rot" as string]: delay > 0.3 ? "-4deg" : "3deg",
    }}>
      {/* Notch */}
      <div style={{ width: 60, height: 8, borderRadius: 999, background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", margin: "0 auto 10px" }} />
      {/* Screen */}
      <div style={{ borderRadius: 20, overflow: "hidden", background: "#111", minHeight: 360, animation: "screenGlow 4s ease-in-out infinite" }}>
        {/* Header bar */}
        <div style={{ background: "#FF1C35", padding: "10px 12px", display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#fff" }}>M</div>
          <span style={{ color: "#fff", fontSize: 11, fontWeight: 800, letterSpacing: 1 }}>MIRE DRAMA</span>
        </div>
        {/* Content thumbnails */}
        {[
          { title: "Permaisuri Terbuang", tag: "🔥 HOT", h: 90 },
          { title: "AKU KAHWIN MAFIA", tag: "✨ NEW", h: 80 },
          { title: "TIPU BALAS", tag: "👑 TOP", h: 80 },
        ].map((item, i) => (
          <div key={i} style={{ margin: "8px 8px 0", borderRadius: 12, overflow: "hidden", background: `linear-gradient(135deg,#1e0005,#1a1a1a)`, padding: "10px 10px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>{item.title}</span>
              <span style={{ fontSize: 8, padding: "2px 5px", borderRadius: 999, background: "rgba(255,28,53,0.3)", color: "#FF1C35", fontWeight: 700 }}>{item.tag}</span>
            </div>
            <div style={{ height: 4, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 999, background: `linear-gradient(90deg,#FF1C35,#ff6b6b)`, width: `${60 + i * 15}%` }} />
            </div>
          </div>
        ))}
        {/* Bottom nav */}
        <div style={{ display: "flex", justifyContent: "space-around", padding: "10px 8px 6px", marginTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          {["🏠","🔍","▶️","💎","👤"].map((ic, i) => (
            <div key={i} style={{ fontSize: 14, opacity: i === 0 ? 1 : 0.35 }} />
          ))}
          {["🏠","🔍","▶️","💎","👤"].map((ic, i) => (
            <span key={i} style={{ fontSize: 13, opacity: i === 0 ? 1 : 0.35 }}>{ic}</span>
          ))}
        </div>
      </div>
      {/* Home indicator */}
      <div style={{ width: 50, height: 4, borderRadius: 999, background: "rgba(255,255,255,0.2)", margin: "10px auto 0" }} />
    </div>
  </motion.div>
);

const AppDownload = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="download" ref={ref} style={{
      padding: "110px 40px",
      borderTop: "1px solid rgba(255,255,255,0.05)",
      position: "relative", overflow: "hidden",
    }}>
      {/* BG effects */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 80% 60% at 50% 50%,rgba(255,28,53,0.07) 0%,transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(255,28,53,0.5),transparent)" }} />

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }} className="app-dl-grid">

          {/* LEFT: Text + buttons */}
          <motion.div initial={{ opacity: 0, x: -30 }} animate={visible ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
              <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>DOWNLOAD · 立即下载</span>
            </div>

            <h2 style={{ fontSize: "clamp(30px,4.5vw,52px)", fontWeight: 900, lineHeight: 1.08, margin: "0 0 16px" }}>
              Watch Anytime.<br />
              <span style={{ color: "#FF1C35" }}>Anywhere.</span>
            </h2>
            <p style={{ fontSize: 16, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, marginBottom: 10 }}>
              Download MIRE DRAMA — Southeast Asia's #1 AI-powered short drama platform. Thousands of episodes, zero wait.
            </p>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.25)", lineHeight: 1.7, marginBottom: 36 }}>
              立即下载 MIRE DRAMA，随时随地畅享东南亚最大本地短剧平台。
            </p>

            {/* Feature pills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 40 }}>
              {features.map(f => (
                <span key={f.en} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "7px 14px", borderRadius: 999,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  fontSize: 12, color: "rgba(255,255,255,0.6)", fontWeight: 500,
                }}>
                  <span>{f.icon}</span>{f.en}
                  <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 10 }}>· {f.zh}</span>
                </span>
              ))}
            </div>

            {/* Download Buttons */}
            <div style={{ display: "flex", flexDirection: "row", gap: 16, flexWrap: "wrap" }}>
              {/* App Store Official Badge */}
              <a
                href="https://apps.apple.com/cn/app/mire-drama/id6751633685"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <motion.div
                  whileHover={{ scale: 1.04, boxShadow: "0 0 24px rgba(255,28,53,0.35)" } as never}
                  style={{
                    display: "flex", alignItems: "center", gap: 13,
                    padding: "11px 22px", borderRadius: 14, cursor: "pointer",
                    background: "#000",
                    border: "1px solid rgba(255,255,255,0.18)",
                    transition: "all 0.25s",
                    minWidth: 190,
                  }}
                >
                  {/* Official Apple logo SVG */}
                  <svg width="26" height="32" viewBox="0 0 814 1000" fill="white">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.3-168.6-107.4C67.3 772.7 1 651.4 1 536.2c0-188.1 123.3-287.7 244.6-287.7 62.7 0 114.9 41.4 154.5 41.4 37.9 0 97.9-43.9 170.2-43.9 27.2 0 109.2 2.6 166 98.8zm-265.4-194.4c31.3-37.3 53.5-89.3 53.5-141.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28 31.3-55.1 83.2-55.1 135.9 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.1-69.7z"/>
                  </svg>
                  <div>
                    <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.5)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 2 }}>Download on the</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", letterSpacing: 0.2, lineHeight: 1 }}>App Store</div>
                  </div>
                </motion.div>
              </a>

              {/* Google Play Official Badge */}
              <a
                href="https://play.google.com/store/apps/details?id=com.miredrama.app&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <motion.div
                  whileHover={{ scale: 1.04, boxShadow: "0 0 24px rgba(255,28,53,0.35)" } as never}
                  style={{
                    display: "flex", alignItems: "center", gap: 13,
                    padding: "11px 22px", borderRadius: 14, cursor: "pointer",
                    background: "#000",
                    border: "1px solid rgba(255,255,255,0.18)",
                    transition: "all 0.25s",
                    minWidth: 190,
                  }}
                >
                  {/* Official Google Play colorful logo */}
                  <svg width="28" height="28" viewBox="0 0 48 48">
                    <linearGradient id="gp1" x1="7.071" y1="14.99" x2="29.4" y2="-7.34" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#00A0FF"/>
                      <stop offset=".007" stopColor="#00A1FF"/>
                      <stop offset=".26" stopColor="#00BEFF"/>
                      <stop offset=".512" stopColor="#00D2FF"/>
                      <stop offset=".76" stopColor="#00DFFF"/>
                      <stop offset="1" stopColor="#00E3FF"/>
                    </linearGradient>
                    <path fill="url(#gp1)" d="M7.076 7.41C6.725 7.79 6.5 8.37 6.5 9.13v29.74c0 .76.225 1.34.576 1.72l.09.088 16.65-16.65v-.392L7.166 7.322l-.09.088z"/>
                    <linearGradient id="gp2" x1="33.835" y1="24" x2="5.13" y2="24" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#FFE000"/>
                      <stop offset=".409" stopColor="#FFBD00"/>
                      <stop offset=".775" stopColor="#FFA500"/>
                      <stop offset="1" stopColor="#FF9C00"/>
                    </linearGradient>
                    <path fill="url(#gp2)" d="m29.052 24.39-5.54-5.542v-.393l5.54-5.542.125.07 6.562 3.73c1.875 1.065 1.875 2.807 0 3.872l-6.562 3.73-.125.075z"/>
                    <linearGradient id="gp3" x1="21.863" y1="26.54" x2="3.583" y2="44.82" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#FF3A44"/>
                      <stop offset="1" stopColor="#C31162"/>
                    </linearGradient>
                    <path fill="url(#gp3)" d="m29.177 24.315-5.665-5.665-16.436 16.436c.618.654 1.638.735 2.79.082l19.31-10.853"/>
                    <linearGradient id="gp4" x1="4.475" y1="2.87" x2="17.796" y2="16.19" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#32A071"/>
                      <stop offset=".069" stopColor="#2DA771"/>
                      <stop offset=".476" stopColor="#15CF74"/>
                      <stop offset=".801" stopColor="#06E775"/>
                      <stop offset="1" stopColor="#00F076"/>
                    </linearGradient>
                    <path fill="url(#gp4)" d="M29.177 23.685 9.866 12.832c-1.152-.653-2.172-.572-2.79.082L23.512 29.35l5.665-5.665z"/>
                  </svg>
                  <div>
                    <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.5)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 2 }}>Get it on</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", letterSpacing: 0.2, lineHeight: 1 }}>Google Play</div>
                  </div>
                </motion.div>
              </a>
            </div>

            {/* Rating */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#FF1C35", fontFamily: "JetBrains Mono,monospace" }}>4.8</div>
                <div style={{ display: "flex", gap: 2, justifyContent: "center", margin: "2px 0" }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} style={{ color: i < 4 ? "#FF1C35" : "rgba(255,28,53,0.4)", fontSize: 11 }}>★</span>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 0.5 }}>RATING</div>
              </div>
              <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.08)" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#FF1C35", fontFamily: "JetBrains Mono,monospace" }}>500K+</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 0.5, marginTop: 4 }}>DOWNLOADS</div>
              </div>
              <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.08)" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#FF1C35", fontFamily: "JetBrains Mono,monospace" }}>MY · ID</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 0.5, marginTop: 4 }}>AVAILABLE IN</div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: Phone mockups */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 24, position: "relative" }}>
            {/* Glow */}
            <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,28,53,0.18) 0%,transparent 70%)", filter: "blur(40px)", pointerEvents: "none" }} />

            {/* Back phone (slightly behind) */}
            {visible && (
              <div style={{ marginBottom: 40, opacity: 0.75 }}>
                <PhoneMockup accent="#ff6b6b" delay={0.15} />
              </div>
            )}
            {/* Front phone */}
            {visible && <PhoneMockup accent="#FF1C35" delay={0.05} />}
          </div>
        </div>
      </div>

      <style>{`
        @media(max-width:768px){.app-dl-grid{grid-template-columns:1fr!important;gap:48px!important}}
      `}</style>
    </section>
  );
};

export default AppDownload;
